import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Text,
  Flex,
  Box,
  Icon,
  Circle,
  Input,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react'
import { ChevronDownIcon, ChevronUpIcon, CheckIcon, SearchIcon } from '@chakra-ui/icons'
import { MdLocationOn } from 'react-icons/md'
import { useAppDispatch, useAppSelector } from '../../app/typedReduxHooks'
import { useOrganizationLocations } from '../../features/locations/hooks/useOrganizationLocations'
import { usePrescriberLocationProfileSearch } from '../../features/locations/hooks/usePrescriberLocationProfileSearch'
import { setWorkLocationId } from '../../pages/NewScriptPage/ScriptForm/PrescriberSlice'
import { useMemo } from 'react'
import { useHasEntitlement } from '../../pages/OrganizationProfilePage/planEntitlements'
import { FeatureKey } from '../../types/organization-entitlements'

export const LocationSwitcher = () => {
  const dispatch = useAppDispatch()
  const hasMultipleLocations = useHasEntitlement(FeatureKey.MULTIPLE_LOCATIONS)
  const workLocationId = useAppSelector((s) => s.prescriber.workLocationId)
  const { joinedPrescriberLocationProfiles } = useOrganizationLocations()
  const { searchQuery, setSearchQuery, filteredProfiles } = usePrescriberLocationProfileSearch(
    joinedPrescriberLocationProfiles
  )

  const currentPrescriberLocationProfile = useMemo(
    () => joinedPrescriberLocationProfiles.find((p) => p.locationId === workLocationId),
    [joinedPrescriberLocationProfiles, workLocationId]
  )

  if (!hasMultipleLocations || joinedPrescriberLocationProfiles.length === 0) return null

  return (
    <Box mb={3}>
      <Text fontSize="xs" fontWeight="bold" color="whiteAlpha.600" mb={1.5} letterSpacing="wide">
        MENU
      </Text>
      <Menu matchWidth onClose={() => setSearchQuery('')}>
        {({ isOpen }) => (
          <>
            <MenuButton
              as={Button}
              variant="outline"
              w="100%"
              h="auto"
              py={1.5}
              px={2.5}
              fontSize="xs"
              borderColor="whiteAlpha.300"
              bg="whiteAlpha.50"
              borderRadius="md"
              _hover={{ bg: 'whiteAlpha.200' }}
              _active={{ bg: 'whiteAlpha.300' }}
              rightIcon={
                isOpen ? (
                  <ChevronUpIcon color="white" boxSize={3} />
                ) : (
                  <ChevronDownIcon color="white" boxSize={3} />
                )
              }
            >
              <Flex align="center" w="100%">
                <Circle size="24px" bg="whiteAlpha.200" mr={2}>
                  <Icon as={MdLocationOn} color="red.400" boxSize={3.5} />
                </Circle>
                <Box textAlign="left" flex="1" overflow="hidden">
                  <Text
                    fontSize="9px"
                    color="whiteAlpha.700"
                    fontWeight="bold"
                    letterSpacing="wider"
                    lineHeight="tight"
                  >
                    LOCATION
                  </Text>
                  <Text
                    fontSize="xs"
                    color="white"
                    fontWeight="semibold"
                    isTruncated
                    lineHeight="short"
                  >
                    {currentPrescriberLocationProfile?.location.name || 'Select Location'}
                  </Text>
                </Box>
              </Flex>
            </MenuButton>
            <MenuList
              p={0}
              overflow="hidden"
              borderRadius="lg"
              boxShadow="lg"
              border="none"
              zIndex={10}
              fontSize="xs"
            >
              <Box bg="white" px={3} py={2} borderBottomWidth="1px" borderColor="gray.100">
                <Text
                  fontSize="10px"
                  fontWeight="bold"
                  color="gray.500"
                  letterSpacing="wide"
                  mb={2}
                >
                  SWITCH LOCATION
                </Text>
                <InputGroup size="sm">
                  <InputLeftElement pointerEvents="none" h="28px" w="28px">
                    <SearchIcon color="gray.400" boxSize={3} />
                  </InputLeftElement>
                  <Input
                    placeholder="Search locations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    borderRadius="md"
                    bg="gray.50"
                    border="none"
                    h="28px"
                    fontSize="xs"
                    pl={8}
                    _focus={{ bg: 'white', ring: 1, ringColor: 'blue.400' }}
                  />
                </InputGroup>
              </Box>
              <Box maxH="300px" overflowY="auto">
                {filteredProfiles.length > 0 ? (
                  filteredProfiles.map((profile) => {
                    const isSelected = profile.locationId === workLocationId
                    return (
                      <MenuItem
                        key={profile.id}
                        onClick={() => dispatch(setWorkLocationId(profile.locationId))}
                        py={2}
                        px={3}
                        minH="36px"
                        bg={isSelected ? 'blue.50' : 'white'}
                        _hover={{ bg: isSelected ? 'blue.100' : 'gray.50' }}
                        borderBottomWidth="1px"
                        borderColor="gray.100"
                        _last={{ borderBottomWidth: 0 }}
                      >
                        <Flex align="center" w="100%">
                          <Circle size="26px" bg={isSelected ? 'blue.100' : 'red.50'} mr={2.5}>
                            <Icon
                              as={MdLocationOn}
                              color={isSelected ? 'blue.500' : 'red.400'}
                              boxSize={3.5}
                            />
                          </Circle>
                          <Text
                            flex="1"
                            fontWeight="semibold"
                            fontSize="xs"
                            color={isSelected ? 'blue.700' : 'gray.800'}
                          >
                            {profile.location.name}
                          </Text>
                          {isSelected && <CheckIcon color="blue.500" boxSize={3} />}
                        </Flex>
                      </MenuItem>
                    )
                  })
                ) : (
                  <Box py={4} px={4} textAlign="center">
                    <Text fontSize="sm" color="gray.500">
                      No locations found
                    </Text>
                  </Box>
                )}
              </Box>
            </MenuList>
          </>
        )}
      </Menu>
    </Box>
  )
}
